import {
  ChevronDown,
  Images,
  Loader2,
  Paintbrush,
  PlusCircle,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  BrandColorPickerPanel,
  type BrandKitColorPickerContext,
  type BrandColorShortcut,
} from "./BrandColorShortcuts";

interface ImageSourceButtonProps {
  onUpload: () => void;
  onBrowseLibrary?: () => void;
  onChangeColor?: (color: string) => void;
  currentColor?: string;
  label: string;
  disabled?: boolean;
  loading?: boolean;
  showPlusIcon?: boolean;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?:
    | "default"
    | "outline"
    | "ghost"
    | "link"
    | "destructive"
    | "secondary";
  align?: "start" | "center" | "end";
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
}

/**
 * Reusable button component for selecting image sources (upload, browse library, or fill color).
 * Used for backgrounds, overlays, and changing existing images.
 */
export function ImageSourceButton({
  onUpload,
  onBrowseLibrary,
  onChangeColor,
  currentColor,
  label,
  disabled = false,
  loading = false,
  showPlusIcon = false,
  className = "w-full",
  size = "sm",
  variant = "outline",
  align = "end",
  brandColors,
  brandKitContext,
}: ImageSourceButtonProps) {
  const [colorMode, setColorMode] = useState(false);
  const [pendingColor, setPendingColor] = useState(currentColor || "#000000");
  const [open, setOpen] = useState(false);

  const handleApply = (color: string) => {
    onChangeColor?.(color);
    setOpen(false);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setColorMode(false);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          disabled={disabled || loading}
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : showPlusIcon ? (
            <PlusCircle className="mr-2 h-4 w-4" />
          ) : null}
          {label}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="z-[10000]"
        onCloseAutoFocus={(e) => {
          // Prevent focus jump when closing after color apply
          if (colorMode) e.preventDefault();
        }}
      >
        {colorMode ? (
          <BrandColorPickerPanel
            value={pendingColor}
            onChange={setPendingColor}
            brandColors={brandColors}
            brandKitContext={brandKitContext}
            showActions
            onApply={handleApply}
            onBack={() => setColorMode(false)}
            autoFocus
          />
        ) : (
          <>
            {onChangeColor && currentColor && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  setPendingColor(currentColor || "#000000");
                  setColorMode(true);
                }}
              >
                <Paintbrush className="mr-2 h-4 w-4" />
                Fill with color
                <span
                  className="ml-auto w-4 h-4 rounded-sm border border-border/60 shrink-0"
                  style={{ backgroundColor: currentColor }}
                />
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onUpload}>
              <Upload className="mr-2 h-4 w-4" />
              Upload from computer
            </DropdownMenuItem>
            {onBrowseLibrary && (
              <DropdownMenuItem onClick={onBrowseLibrary}>
                <Images className="mr-2 h-4 w-4" />
                Browse image library
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
